import React from 'react';
import { Typography, Tooltip } from '@mui/material';

interface EllipsisTextProps {
  text: string;
  maxWidth?: number | string;
  variant?: 'body1' | 'body2' | 'h6' | 'subtitle1' | 'subtitle2' | 'caption';
  enterDelay?: number; // ms before tooltip shows
  leaveDelay?: number; // ms before tooltip hides
}
/**
 * EllipsisText
 *
 * A typography wrapper that truncates overflowing text with an ellipsis (`...`)
 * and shows the full text inside a Material UI Tooltip on hover.
 *
 * Props:
 * - text: string (required) – the text to display
 * - maxWidth: number | string (optional) – the max width before truncation, defaults to "100%"
 * - variant: MUI typography variant (optional) – defaults to "body1"
 * - enterDelay: number (optional) – delay in ms before showing tooltip
 * - leaveDelay: number (optional) – delay in ms before hiding tooltip
 *
 * Example usage:
 *
 * ```tsx
 * <EllipsisText
 *   text="This is some very long text that will truncate if it exceeds the max width."
 *   maxWidth={200}
 *   variant="body2"
 *   enterDelay={500} // show after 500ms
 *   leaveDelay={200} // hide after 200ms
 * />
 * ```
 */
const EllipsisText: React.FC<EllipsisTextProps> = ({ text, maxWidth = '100%', variant = 'body1', enterDelay = 1000, leaveDelay = 0 }) => {
  return (
    <Tooltip title={text} enterDelay={enterDelay} leaveDelay={leaveDelay}>
      <Typography
        variant={variant}
        noWrap
        sx={{
          maxWidth,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
};

export default EllipsisText;
